import React, { Fragment, useState, useRef, useMemo, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

import ADND from '@aytm/lib/adnd/ADND'

import { formatAmounts } from '@aytm/app/stats/lib/helpers'
import { getQuestionId, getQuestionIcon } from '@aytm/app/stats/page/components/lib/utils'
import { getIsDefaultReportParams } from '@aytm/app/stats/page/comments/lib/utils'
import { imageAsset as imageAssetUrl } from '@aytm/app/survey/editor/resources/urls'

import htmlReactParser, { domToReact } from 'html-react-parser'
import openSidebarLink, {
  OPEN_SIDEBAR_LINK_NAME,
  checkSidebarLink
} from '@aytm/app/stats/page/question-views/components/lib/skipper-links/open-sidebar'

// components
import Popup from '@aytm/lib/rfd-ui/components/basic/popup'

import QuestionTitle from '@aytm/app/stats/page/question-views/components/QuestionTitle'
import QuestionAnnotation from '@aytm/app/stats/page/question-views/components/QuestionAnnotation'

import ImagePreview from '@aytm/app/survey/editor/components/content/assets/ImagePreview'

// hooks
import useMountedRef from '@aytm/lib/hooks/useMountedRef'
import useSingleAndDoubleClick from '@aytm/app/stats/hooks/useSingleAndDoubleClick'

// styles
import style from '@aytm/app/stats/page/question-views/components/styles/QuestionHeader.module.scss'
import annotationStyle from '@aytm/app/stats/page/question-views/components/styles/QuestionAnnotation.module.scss'

// consts
import {
  IMAGE_ASSET_PATH_FILTER_RESIZE_TO_LIMIT,
  IMAGE_ASSET_PATH_FILTER_RESIZE_AND_PAD
} from '@aytm/lib/common/assets'
import {
  QUESTION_TITLE_THUMB_HEIGHT,
  QUESTION_TITLE_THUMB_WIDTH,
  QUESTION_TITLE_IMAGE_MIN_WIDTH,
  QUESTION_TITLE_IMAGE_MAX_WIDTH,
  QUESTION_TITLE_IMAGE_MIN_HEIGHT,
  QUESTION_TITLE_IMAGE_MAX_HEIGHT,
  TYPE_DND_MAP
} from '@aytm/app/stats/page/question-views/components/lib/constants'

// SVGs
import RadarSVG from '@aytm/app/stats/page/question-views/components/lib/svg/radar.svg'

const QuestionHeader = ({
  model = {},
  question,
  onScrollToSkip,
  reportId,
  features,
  windowUID,
  sortableList,
  questionView,
  isEditingExt,
  titleExt,
  isQuestionCollapsed,
  isReportXpert,
  isShared,
  getReportParams,
  onToggleCollapse,
  onUpdateParams,
  onTitleChange,
  onStorytellingNoteChange,
  oeSearchParams,
  onCloneQuestion,
  onAIAssistantsClick,
  onAIAddQuestionClick,
  onFinalizeDragLayerContent,
  onDndStart,
  onDndEnd,
  onSetTitleExt,
  onSetEditQuestionExt,
  onSaveExt,
  onAIAssistantsCancel
}) => {
  const {
    id,
    key,
    parentID,
    icon,
    type,
    pre_type,
    subType,
    position,
    visual_position,
    text,
    header,
    storytellingNote,
    answer,
    image,
    skipsTo,
    aiAssistantsText,
    aiAssistantsHash,
    aiAssistantsTotalTime,
    aiAssistantsCurrentTime,
    aiProgressMessage,
    disabled
  } = model

  const [title, setTitle] = useState(
    titleExt != undefined && null !== titleExt ? titleExt : ('string' == typeof header && header) || text
  )
  const [note, setNote] = useState(storytellingNote)

  const [isEditing, setIsEditing] = useState(isEditingExt || false)
  const [isCollapsed, setIsCollapsed] = useState(isQuestionCollapsed)
  const [thumbnailPreviewOpened, setThumbnailPreviewOpened] = useState(false)

  const [aiAssistantPopupPosition, setAIAssistantPopupPosition] = useState('topright')
  const [aiAssistantRequestFailed, setAIAssistantRequestFailed] = useState(false)

  const mountedRef = useMountedRef()

  const componentRef = useRef(null)

  const savedValues = useRef(null)
  savedValues.current = {
    text: titleExt != undefined && null !== titleExt ? titleExt : ('string' == typeof header && header) || text,
    note: storytellingNote
  }

  const dndListItemRef = useRef(questionView)
  const dndListItemHandleRef = useRef()

  const isRadarEnabled =
    ['rb', 'cb', 'mx', 'sr', 'se', 'ro', 'aro', 'mp', 'qa', 'bpm', 'psc'].includes(type) && !features.radar.hidden

  const isAIAssistantsEnabled = 'dc' != type && !isShared && !disabled && !features.ai_assistants_stat_summary.disabled

  const aiProgressMessageNode = useMemo(
    () =>
      aiProgressMessage
        ? htmlReactParser(aiProgressMessage, {
            trim: true,
            replace({ name, attribs, children }) {
              if (OPEN_SIDEBAR_LINK_NAME == name) {
                const tab = attribs?.tab || ''
                const focus = attribs?.focus || ''
                const icon = attribs?.icon || ''

                return checkSidebarLink({ tab }) ? (
                  <span
                    className={`${annotationStyle.annotationLink} -dtr-open-sidebar-link -dtr-open-sidebar-link-${tab} -dtr-open-sidebar-link-${focus}`}
                    onClick={() => void openSidebarLink({ tab, focus })}
                  >
                    {icon ? <i className={`${icon} ${annotationStyle.annotationLinkIcon}`} /> : null}
                    {domToReact(children)}
                  </span>
                ) : (
                  <span className={`${annotationStyle.annotationLink} ${annotationStyle.annotationLinkDisabled}`}>
                    {icon ? <i className={`${icon} ${annotationStyle.annotationLinkIcon}`} /> : null}
                    {domToReact(children)}
                  </span>
                )
              }
              return void 0
            }
          })
        : '',
    [aiProgressMessage]
  )

  const isTitleEditable =
    'dc' != type && !isShared && !disabled && ('function' == typeof onTitleChange || 'function' == typeof onSaveExt)
  const isNoteEditable = 'dc' != type && !isShared && !disabled && 'function' == typeof onStorytellingNoteChange
  const isAnnotationEditable = 'dc' != type && !isShared && !disabled && !features.disabled

  const isNoteDisabled =
    isEditing &&
    (!title.trim().length || (title == (('string' == typeof header && header) || text) && note == storytellingNote))

  const isTogglable =
    !isEditing && !thumbnailPreviewOpened && !features.toggling.disabled && 'function' == typeof onToggleCollapse
  const isDraggable =
    'dc' != type && !disabled && !isEditing && !thumbnailPreviewOpened && !features.reorder_questions.disabled
  const isCloneable =
    'dc' != type &&
    !position &&
    !disabled &&
    !isEditing &&
    !thumbnailPreviewOpened &&
    'function' == typeof onCloneQuestion

  const questionId = useMemo(
    () => getQuestionId({ id, type, pre_type, position, visual_position }, parentID).toUpperCase(),
    [id, parentID, type, pre_type, position, visual_position]
  )

  const questionIcon = useMemo(
    () =>
      getQuestionIcon({
        type: `${icon || type}${(model.features != undefined && model.features.insightsMapping && '-mapping') || ''}`,
        pre_type,
        subType
      }),
    [icon, type, pre_type, subType, model.features]
  )

  const onShowCommentsPage = useCallback(
    (event, mode) => {
      event.preventDefault()

      const query = []
      const selectedQuestion = questionId.toLowerCase()
      const searchParams = oeSearchParams

      if (searchParams.selectedQuestion && selectedQuestion != searchParams.selectedQuestion)
        searchParams.searchString = ''
      // the video page is always survey-wide, so its slot never keeps a question
      searchParams.selectedQuestion = 'video-responses' != mode ? selectedQuestion : ''
      searchParams.query = query

      const params = {}
      params[mode] = getIsDefaultReportParams(searchParams, mode) ? null : searchParams

      onUpdateParams(event, params, reportId, mode)
    },
    [questionId, reportId, oeSearchParams, onUpdateParams]
  )

  const onShowRadarPage = useCallback(
    event => {
      event.preventDefault()

      if (isReportXpert || !getReportParams) return
      const params = getReportParams()

      const justFiltersParams = {}
      Object.keys(params).forEach(pk => {
        const pv = params[pk]
        if (/^(filter|virtual|schema|psc\d+)/.test(pk)) {
          justFiltersParams[pk] = pv
        }
      })
      if (id) justFiltersParams['q'] = Number(id)

      let meta = {}
      meta = window.StatPage.lib.addCSRF(meta)
      const url = window.StatPage.lib.getStatUrl(
        window.shareKey && window.shareKey.key ? `stat/${window.shareKey.key}/radar` : 'radar',
        true
      )

      window.$STRG.Encode(justFiltersParams).then(base64params => {
        meta._bsfz = base64params
        window.StatHelpers.DOM.submitHiddenForm(url, meta)
      })
    },
    [id, isReportXpert, getReportParams]
  )

  const dndType =
    0 < position
      ? `question-${windowUID}`
      : `${TYPE_DND_MAP[type]}${model.features != undefined && model.features.insightsMapping ? '-mapping' : ''}`

  const dndListItem = isDraggable
    ? ADND.targets.itemsList
        .listItem({
          type: dndType,
          list: sortableList
        })
        .onFinalizeDragLayerContent(onFinalizeDragLayerContent)
    : null

  const dndListItemHandle = isDraggable
    ? ADND.sources
        .listItemHandle({
          type: dndType,
          item: dndListItem,
          lockX: false,
          cloneable: isCloneable
        })
        .onStart(() => onDndStart(dndType))
        .onEnd(onDndEnd)
        .fetchData(() => {
          return {
            key,
            model: question,
            windowUID
          }
        })
    : null

  const onHeaderSingleClick = () => {
    if (!isTogglable) return
    onToggleCollapse()
  }

  const onHeaderDoubleClick = () => {
    if (!isCloneable) return
    onCloneQuestion()
  }

  const onCancelEditing = () => {
    if ('function' == typeof onSetEditQuestionExt) {
      onSetEditQuestionExt(false)
    } else {
      setTitle(savedValues.current.title)
      setNote(savedValues.current.note)
      setIsEditing(false)
    }
  }

  const onSaveEditing = (withThisNote = note) => {
    if (isNoteEditable && (storytellingNote != withThisNote || note != withThisNote)) {
      // delete line breaks before and after note
      const updatedNote = withThisNote.replace(/^(?:\s*<br\b[^>]*>\s*)+|(?:\s*<br\b[^>]*>\s*)+$/gi, '').trim()
      setNote(updatedNote)
      onStorytellingNoteChange(updatedNote)
    }

    if (isTitleEditable && title != (('string' == typeof header && header) || text)) {
      onTitleChange(title)
    }

    if ('function' == typeof onSetEditQuestionExt) {
      onSetEditQuestionExt(false)
    } else {
      setIsEditing(false)
    }
  }

  const toggleQuestionAnnotation = event => {
    event.preventDefault()

    if ('dc' == type) return
    if (isCollapsed) onHeaderSingleClick()
    if (isEditing) {
      if ('function' == typeof onSaveExt) {
        onSaveExt()
      } else {
        onSaveEditing()
      }
    } else {
      if ('function' == typeof onSetEditQuestionExt) {
        onSetEditQuestionExt(!isEditing)
      } else {
        savedValues.current = { title, note }
        setIsEditing(!isEditing)
      }
    }
  }

  const onSkipToClick = (event, idx) => {
    event.preventDefault()

    if ('dc' == type) return
    if ('function' == typeof onScrollToSkip) {
      onScrollToSkip(idx)
    }
  }

  const onKeyDownHandler = event => {
    if (27 == event.keyCode) {
      onCancelEditing()
    }
  }

  const onHeaderClick = useSingleAndDoubleClick(
    isTogglable ? onHeaderSingleClick : null,
    isCloneable ? onHeaderDoubleClick : null
  )

  const onScroll = () => {
    if (null === componentRef.current) {
      setAIAssistantPopupPosition('topright')
    } else {
      const stickyHeader = document.querySelector('.stats-StickyHeader')

      if (stickyHeader) {
        const header = document.querySelector('#header')
        const headerRect = header.getBoundingClientRect()

        const stickyHeaderRect = stickyHeader.getBoundingClientRect()

        const componentRect = componentRef.current.getBoundingClientRect()
        setAIAssistantPopupPosition(
          componentRect.y < headerRect.y + headerRect.height + stickyHeaderRect.height + 220
            ? 'bottomright'
            : 'topright'
        )
      }
    }
  }

  useEffect(() => {
    if (aiAssistantRequestFailed) {
      document.addEventListener('scroll', onScroll, false)
      document.defaultView.addEventListener('resize', onScroll, false)
    }
    onScroll()

    return () => {
      document.removeEventListener('scroll', onScroll, false)
      document.defaultView.removeEventListener('resize', onScroll, false)
    }
  }, [aiAssistantRequestFailed])

  useEffect(() => {
    if (isEditing) window.addEventListener('keydown', onKeyDownHandler)
    else window.removeEventListener('keydown', onKeyDownHandler)
    return () => {
      window.removeEventListener('keydown', onKeyDownHandler)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing])

  useEffect(() => {
    if (note != storytellingNote) {
      setNote(storytellingNote)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storytellingNote])

  useEffect(() => {
    if (isEditing && isQuestionCollapsed) {
      onCancelEditing()
    }
    setIsCollapsed(isQuestionCollapsed)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isQuestionCollapsed])

  useEffect(() => {
    setTitle(
      titleExt != undefined && null !== titleExt
        ? titleExt
        : isEditing
          ? title
          : ('string' == typeof header && header) || text
    )
  }, [titleExt, text, header, title, isEditing])

  useEffect(() => {
    if (null !== isEditingExt) {
      setIsEditing(isEditingExt)
    }
  }, [isEditingExt])

  useEffect(() => {
    if (null !== aiAssistantsHash) {
      onCancelEditing()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiAssistantsHash])

  useEffect(() => {
    if (dndListItem) dndListItem.connect(dndListItemRef.current)

    return () => {
      if (dndListItem) dndListItem.disconnect()
    }
  }, [dndListItem])

  useEffect(() => {
    if (dndListItemHandle) dndListItemHandle.connect(dndListItemHandleRef.current)

    return () => {
      if (dndListItemHandle) dndListItemHandle.disconnect()
    }
  }, [dndListItemHandle])

  useEffect(() => {
    if (dndListItemHandle)
      dndListItemHandle.fetchData(() => {
        return {
          key,
          model: question,
          windowUID
        }
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question])

  const renderQuestionThumbnail = () => (
    <div className={style.thumbnail}>
      <Popup
        className={style['thumbnail-popup']}
        trigger="hover"
        direction="left"
        portal
        hideOtherPopupsOnShow
        content={() => (
          <ImagePreview
            minWidth={`${QUESTION_TITLE_IMAGE_MIN_WIDTH}px`}
            maxWidth={`${QUESTION_TITLE_IMAGE_MAX_WIDTH}px`}
            minHeight={`${QUESTION_TITLE_IMAGE_MIN_HEIGHT}px`}
            maxHeight={`${QUESTION_TITLE_IMAGE_MAX_HEIGHT}px`}
            imageUrl={image.original.url}
            onImageOpen={() => setThumbnailPreviewOpened(true)}
            onImageHide={() => setThumbnailPreviewOpened(false)}
            previewUrl={imageAssetUrl(image.original.path, {
              width: QUESTION_TITLE_IMAGE_MAX_WIDTH,
              height: QUESTION_TITLE_IMAGE_MAX_HEIGHT,
              filter: IMAGE_ASSET_PATH_FILTER_RESIZE_TO_LIMIT,
              unsharp: true,
              backgroundColor: 'transparent'
            })}
          />
        )}
        contentTheme="thin-black"
      >
        <img
          src={imageAssetUrl(image.original.path, {
            width: QUESTION_TITLE_THUMB_WIDTH,
            height: QUESTION_TITLE_THUMB_HEIGHT,
            filter: IMAGE_ASSET_PATH_FILTER_RESIZE_AND_PAD,
            unsharp: true,
            backgroundColor: 'white'
          })}
        />
      </Popup>
    </div>
  )

  const renderRadarLink = () => {
    const msg = features.radar.available ? (
      <p>
        {/* PME2 (AYTM-11321): render server-authored `text` verbatim; guard the icon, which the
            already-on-Enterprise Contact-Us variant omits. */}
        {features.radar.onPackage.icon ? (
          <span
            className={classNames('stat-upgrade-icon', {
              [features.radar.onPackage.id]: true
            })}
          >
            <i className={`icon-aytmfd-${features.radar.onPackage.icon}`} />
          </span>
        ) : null}
        <span dangerouslySetInnerHTML={{ __html: features.radar.onPackage.text }} />
      </p>
    ) : features.radar.disabled ? (
      'PLEASE LOGIN/JOIN THE TEAM TO UNLOCK ALL FEATURES <br/>OF THE STAT REPORT'
    ) : null

    return (
      <div
        className={classNames(style.radar, {
          [style.disabled]: features.radar.disabled
        })}
      >
        <Popup
          width="254px"
          trigger={null !== msg ? 'hover' : 'none'}
          direction="bottomright"
          show={null !== msg ? null : false}
          hideOtherPopupsOnShow
          content={() => (
            <div
              dangerouslySetInnerHTML={{
                __html: null !== msg ? msg : ''
              }}
            />
          )}
        >
          {features.radar.available ? (
            <a className={style.aRadar} href={features.radar.onPackage.upgradeURL} rel="noreferrer">
              <img src={RadarSVG} alt="Radar" />
            </a>
          ) : (
            <span className={style.aRadar} onClick={features.radar.disabled ? null : onShowRadarPage}>
              <img src={RadarSVG} alt="Radar" />
            </span>
          )}
        </Popup>
      </div>
    )
  }

  // TODO remove window.StatPage
  const renderSkipToLinks = () => {
    let res
    if (skipsTo.length > 1) {
      const qSkipsLinks = skipsTo.map(qSkipTo => {
        let item
        if (qSkipTo > 0) {
          item = (
            <span key={`skipTo${qSkipTo}`} onClick={event => onSkipToClick(event, qSkipTo)}>
              {window.StatPage.lib.questionPositionToVP(qSkipTo)}
            </span>
          )
        } else if (-1 == qSkipTo) {
          item = 'EXIT'
        } else if (-99 == qSkipTo) {
          item = 'HIDDEN EXIT'
        }
        return item
      })
      res = (
        <>
          <span className={style.aSkipTo}>
            <i className="icon-aytmfd-random" />
          </span>
          <span className={style.spanSkipTo}>
            (
            {qSkipsLinks.map((qSkipLink, idx) => (
              <Fragment key={`skipToLink${idx}`}>
                {idx ? ' or ' : null}
                {qSkipLink}
                {0 == (idx + 1) % 3 && qSkipsLinks.length > idx + 1 ? <br /> : null}
              </Fragment>
            ))}
            )
          </span>
        </>
      )
    } else {
      res = (
        <>
          <span className={style.aSkipTo} onClick={event => onSkipToClick(event, skipsTo[0])}>
            <i className="icon-aytmfd-skip" />
          </span>
          <span className={style.spanSkipTo} onClick={event => onSkipToClick(event, skipsTo[0])}>
            {-1 == skipsTo[0]
              ? 'EXIT'
              : -99 == skipsTo[0]
                ? 'HIDDEN EXIT'
                : window.StatPage.lib.questionPositionToVP(skipsTo[0])}
          </span>
        </>
      )
    }

    return (
      <div
        className={classNames(style.skipTo, {
          [style.random]: skipsTo.length > 1,
          [style.exit]: 1 == skipsTo.length && skipsTo[0] < 0
        })}
      >
        {res}
      </div>
    )
  }

  const onAIAssistantsClickCallback = data => {
    if (mountedRef.current) {
      setAIAssistantRequestFailed(
        null === data || 'failed' == data.scope?.aiStatus || (!data.response && !data.scope?.summary)
      )
    }
  }

  return (
    <div ref={componentRef} className={classNames(style.container, isCollapsed && style.collapsed)}>
      {isTogglable && (
        <span className={`${style.collapseLink} head-expand`} onClick={onToggleCollapse}>
          {isCollapsed ? 'Expand' : 'Collapse'}
        </span>
      )}
      <div className={classNames(style.component, isTogglable && style.togglable, isDraggable && style.draggable)}>
        <div className={style.subcomponent} onClick={onHeaderClick}>
          {'dc' != type && (
            <>
              <div className={style.questionType}>
                <i className={`icon-aytmfd-${questionIcon}`} />
              </div>
              <div className={style.questionId}>{questionId}</div>
            </>
          )}

          <div className={style.questionTitleContainer}>
            <QuestionTitle
              question={model}
              title={title}
              editing={isEditing}
              disabled={!isTitleEditable}
              setTitle={'function' == typeof onSetTitleExt ? onSetTitleExt : setTitle}
            />
          </div>
        </div>

        {image != undefined && image.original != undefined ? renderQuestionThumbnail(image) : null}

        {['oe', 'mn'].includes(type) && !features.comments_page.hidden ? (
          <span
            id={`comments_${position}`}
            className={classNames(
              style.comments,
              ('loading' == answer || !answer.comments || features.comments_page.disabled) && style['comments-disabled']
            )}
            title={
              !features.comments_page.disabled
                ? 'loading' != answer && null !== answer.comments && 0 < answer.comments
                  ? `View ${formatAmounts(answer, 'comments')} custom answers`
                  : 'No custom answers'
                : ''
            }
            onClick={
              'loading' != answer && null !== answer.comments && 0 < answer.comments && !features.comments_page.disabled
                ? event => onShowCommentsPage(event, 'comments')
                : null
            }
          >
            {'loading' != answer && null !== answer.comments && 0 < answer.comments
              ? formatAmounts(answer, 'comments')
              : 0}
            <i className="icon-aytmfd-comments-v2" />
          </span>
        ) : null}

        {'cn' == type && !features.comments_page.hidden ? (
          <span
            id={`comments_${position}`}
            className={classNames(
              style.comments,
              ('loading' == answer || !answer.comments || features.comments_page.disabled) && style['comments-disabled']
            )}
            title={
              !features.comments_page.disabled
                ? 'loading' != answer && null !== answer.comments && 0 < answer.comments
                  ? `View ${formatAmounts(answer, 'comments')} custom answers`
                  : 'No custom answers'
                : ''
            }
            onClick={
              'loading' != answer && null !== answer.comments && 0 < answer.comments && !features.comments_page.disabled
                ? event => onShowCommentsPage(event, 'comments')
                : null
            }
          >
            {'loading' != answer && null !== answer.comments && 0 < answer.comments
              ? formatAmounts(answer, 'comments')
              : 0}
            <i className="icon-aytmfd-comments-v2" />
          </span>
        ) : null}

        {'ir' == type && !features.comments_page.hidden ? (
          <span
            id={`image_responses_${position}`}
            className={classNames(
              style.imageResponses,
              ('loading' == answer || !answer.images || features.comments_page.disabled) &&
                style['imageResponses-disabled']
            )}
            title={
              !features.comments_page.disabled
                ? 'loading' != answer && null !== answer.images && 0 < answer.images
                  ? `View ${formatAmounts(answer, 'images')} image responses`
                  : 'No image responses'
                : ''
            }
            onClick={
              'loading' != answer && null !== answer.images && 0 < answer.images && !features.comments_page.disabled
                ? event => onShowCommentsPage(event, 'image-responses')
                : null
            }
          >
            {'loading' != answer && null !== answer.images && 0 < answer.images ? formatAmounts(answer, 'images') : 0}
            <i className="icon-aytmfd-image-responses-v2" />
          </span>
        ) : null}

        {'va' == type && !features.comments_page.hidden ? (
          <span
            id={`videos_${position}`}
            className={classNames(
              style.videoResponses,
              ('loading' == answer || !answer.videos || features.comments_page.disabled) &&
                style['videoResponses-disabled']
            )}
            title={
              !features.comments_page.disabled
                ? 'loading' != answer && null !== answer.videos && 0 < answer.videos
                  ? `View ${formatAmounts(answer, 'videos')} videos`
                  : 'No videos responses'
                : ''
            }
            onClick={
              'loading' != answer && null !== answer.videos && 0 < answer.videos && !features.comments_page.disabled
                ? event => onShowCommentsPage(event, 'video-responses')
                : null
            }
          >
            {'loading' != answer && null !== answer.videos && 0 < answer.videos ? formatAmounts(answer, 'videos') : 0}
            <i className="icon-aytmfd-video-responses-v2" />
          </span>
        ) : null}

        {skipsTo != undefined && skipsTo.length ? renderSkipToLinks() : null}

        {isAIAssistantsEnabled ? (
          <>
            {aiAssistantRequestFailed ? (
              <Popup
                targetClassName={style['aiAssistants-requestFailedPopupTarget']}
                width="400px"
                trigger="none"
                popupOffset={-36}
                direction={aiAssistantPopupPosition}
                show
                hideOtherPopupsOnShow={false}
                content={() => (
                  <div
                    className={style['aiAssistants-requestFailedPopupContent']}
                    onClick={() => void setAIAssistantRequestFailed(false)}
                  >
                    <i className={`icon-aytmfd-close-v2 ${style['aiAssistants-requestFailedPopupClose']}`} />
                    <i className={`icon-aytmfd-skipper ${style['aiAssistants-requestFailedPopupIcon']}`} />
                    <b>
                      Sorry, mate, I&apos;ve lost my bearings...&nbsp;&nbsp;&nbsp;
                      <span className={style['aiAssistants-requestFailedPopupCompass']}>🧭</span>
                    </b>
                    <br />
                    <small>Let&apos;s give it another go in a bit.</small>
                  </div>
                )}
              >
                <span className={style['aiAssistants-requestFailedPopupAnchor']} />
              </Popup>
            ) : null}
            <span
              className={classNames(
                'stat-skipper-question-button',
                '-dtr-stat-skipper-question-button',
                `-dtr-stat-skipper-question-button--${type || 'unknown'}`,
                style.aiAssistants,
                aiAssistantsHash && style['aiAssistants-processing'],
                aiAssistantsHash && style['aiAssistants-disabled']
              )}
              data-skipper-question-button={questionId}
              onClick={
                aiAssistantsHash
                  ? null
                  : () => void (setAIAssistantRequestFailed(false), onAIAssistantsClick(onAIAssistantsClickCallback))
              }
            >
              {aiAssistantsHash && (
                <span
                  className={style['aiAssistants-fill']}
                  style={{
                    width: 0 < aiAssistantsTotalTime ? `${(100 * aiAssistantsCurrentTime) / aiAssistantsTotalTime}%` : 0
                  }}
                ></span>
              )}
              <i className="icon-aytmfd-skipper" />
              <span
                className={style['aiAssistants-text']}
                dangerouslySetInnerHTML={{ __html: aiAssistantsText }}
              ></span>
            </span>
          </>
        ) : null}

        {isAnnotationEditable ? (
          <span
            id={`stat-annotation-edit-button-${id}`}
            className={classNames(
              'stat-annotation-edit-button -dtr-stat-annotation-edit-button',
              style.titleEdit,
              aiAssistantsHash && style['titleEdit-disabled']
            )}
            onClick={aiAssistantsHash ? null : toggleQuestionAnnotation}
          >
            <i className="icon-aytmfd-pencil" />
          </span>
        ) : null}

        {isRadarEnabled ? renderRadarLink() : null}
      </div>

      {isDraggable && <div ref={dndListItemHandleRef} className={style.questionMove} onClick={onHeaderClick} />}

      {!isCollapsed && aiProgressMessage ? (
        <div className={style.aiProgressMessage}>
          {aiProgressMessageNode}
          {onAIAssistantsCancel ? (
            <>
              <br />
              <br />
              <div className={style.aiCancelButton} onClick={() => void onAIAssistantsCancel()}>
                Cancel
              </div>
            </>
          ) : null}
        </div>
      ) : null}

      {!isCollapsed && !onSaveExt && !aiProgressMessage && (
        <QuestionAnnotation
          id={id}
          note={note}
          editing={isEditing}
          isNoteEditable={isNoteEditable}
          disabled={isNoteDisabled}
          setNote={setNote}
          onSaveEditing={onSaveEditing}
          onCancelEditing={onCancelEditing}
          onAIAddQuestionClick={onAIAddQuestionClick}
        />
      )}
    </div>
  )
}

QuestionHeader.propTypes = {
  model: PropTypes.shape({
    id: PropTypes.string,
    key: PropTypes.string,
    parentID: PropTypes.string,
    disabled: PropTypes.bool,
    type: PropTypes.string,
    subType: PropTypes.string,
    pre_type: PropTypes.number,
    position: PropTypes.number,
    visual_position: PropTypes.number,
    header: PropTypes.string,
    text: PropTypes.string,
    textAppendix: PropTypes.string,
    answer: PropTypes.any,
    image: PropTypes.object,
    skipsTo: PropTypes.array,
    aiProgressMessage: PropTypes.string
  }),
  question: PropTypes.object,
  isReportXpert: PropTypes.bool,
  isShared: PropTypes.bool,
  reportId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  sortableList: PropTypes.object,
  features: PropTypes.object,
  isQuestionCollapsed: PropTypes.bool,
  windowUID: PropTypes.string,
  isEditingExt: PropTypes.bool,
  titleExt: PropTypes.string,
  questionView: PropTypes.any,
  getReportParams: PropTypes.func,
  oeSearchParams: PropTypes.object,
  onScrollToSkip: PropTypes.func,
  onToggleCollapse: PropTypes.func,
  onUpdateParams: PropTypes.func,
  onCloneQuestion: PropTypes.func,
  onAIAssistantsClick: PropTypes.func,
  onAIAddQuestionClick: PropTypes.func,
  onFinalizeDragLayerContent: PropTypes.func,
  onDndStart: PropTypes.func,
  onDndEnd: PropTypes.func,
  onTitleChange: PropTypes.func,
  onStorytellingNoteChange: PropTypes.func,
  onSetEditQuestionExt: PropTypes.func,
  onSaveExt: PropTypes.func,
  onSetTitleExt: PropTypes.func,
  onAIAssistantsCancel: PropTypes.func
}

QuestionHeader.displayName = 'QuestionHeader'

export default QuestionHeader

ADND().connect()
